// useRegionsMap — TanStack Query wrapper around POST /v1/map/regions (T9).
//
// Powers the Intel map's `contracts-region` choropleth (T11). Returns the
// per-region contract metrics for the regions intersecting the current
// viewport at the zoom-derived hierarchy level. The layer joins these to
// the region MVT polygons (served by tile-edge /v1/regions) on region_id.
//
// Viewport-driven: the request body carries {level, bbox, state}. `level`
// is `levelForZoom(zoom)` (packages/geo) so the metric granularity tracks
// the map's region tiles — country at z<3, admin1 at z<6, etc. We refetch
// when level OR bbox changes (the query key includes both); panning within
// a level still refetches because the bbox shifts, which is correct — a new
// viewport intersects a different region set.
//
// Default-OFF gate: like useContractsMap, we only fire when the contracts
// layer is enabled in the legend, so the page mount doesn't burn a request.
//
// Filter-aware: the same FilterState the rail emits is posted; the server
// compiles the awards+contracts intersection. Against the stub MV (T7) the
// counts are 0 until T10 wires the MV body — the layer then renders gray
// "no data" polygons, which is the correct pre-T10 state.

import { API_BASE_URL } from '@/lib/env';
import { readAccessToken } from '@/state/auth';
import type { filters as f } from '@eidollon/contracts';
import { levelForZoom } from '@eidollon/geo';
import type { RegionFeature } from '@eidollon/intel-feed-client';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

// Debounce the viewport bbox before it enters the query key. Panning/zooming
// fires a new bbox on every animation frame; without debouncing each one
// keys a fresh POST /v1/map/regions, so a single drag spawns dozens of
// requests. We hold the bbox until the viewport settles for `delayMs`, then
// commit it — rapid panning coalesces into one request when the map stops.
function useDebouncedBbox(
  bbox: [number, number, number, number],
  delayMs: number,
): [number, number, number, number] {
  const [debounced, setDebounced] = useState(bbox);
  // Serialize so the effect only re-runs when the bbox values actually change,
  // not when the caller passes a fresh array of identical numbers each render.
  const key = bbox.join(',');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(bbox), delayMs);
    return () => clearTimeout(t);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `key` is the value-identity of `bbox`; depending on the array itself would re-arm the timer every render.
  }, [key, delayMs]);
  return debounced;
}

interface RegionsFeatureCollection {
  type: 'FeatureCollection';
  features: RegionFeature[];
}

export interface UseRegionsMapResult {
  regionFeatures: readonly RegionFeature[];
  isLoading: boolean;
  error: string | null;
}

const EMPTY: readonly RegionFeature[] = [];

export function useRegionsMap(
  enabled: boolean,
  zoom: number,
  bbox: [number, number, number, number],
  filterState: f.FilterState,
): UseRegionsMapResult {
  const token = readAccessToken() ?? '';
  // levelForZoom drives the request granularity directly. After T6.1, US + CA
  // have polygons at every level (country / admin1 / regional / city). For
  // other seeded countries (GB / FR / AE) only country + admin1 polygons exist
  // — at high zoom levels their regional/city requests return an empty
  // FeatureCollection and the choropleth gracefully renders nothing rather
  // than a stale admin1 fill. That's the right v1 behavior; extending those
  // countries is the natural follow-on to T6.1.
  const level = levelForZoom(zoom);

  // Coalesce rapid pan/zoom into one request when the viewport settles. The
  // debounced bbox is what enters the query key + fetch body, so the choropleth
  // refetches once per settled viewport rather than once per frame.
  const debouncedBbox = useDebouncedBbox(bbox, 300);

  const query = useQuery({
    queryKey: ['map-regions', level, debouncedBbox, filterState, token],
    enabled,
    // Keep the previously-fetched features painted while the next viewport's
    // request is in flight. Without this, query.data goes undefined on every
    // pan/zoom (the bbox is in the key), the layer renders nothing, then
    // repaints when data arrives — a visible blink of the choropleth on every
    // map move. keepPreviousData holds the old fill until the new one is ready.
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<RegionFeature[]> => {
      const res = await fetch(`${API_BASE_URL}/v1/map/regions`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ level, bbox: debouncedBbox, state: filterState }),
      });
      if (!res.ok) throw new Error(`map/regions ${res.status}`);
      const json = (await res.json()) as RegionsFeatureCollection;
      // The response IS a GeoJSON FeatureCollection (geometry + metrics in
      // one payload) — hand the features straight to GeoJsonLayer. Drop any
      // feature missing geometry (can't render a polygon-less choropleth).
      const features = Array.isArray(json?.features) ? json.features : [];
      return features.filter((ft) => ft.geometry != null);
    },
    // 60s so toggling a facet on/off/on within the window is a cache hit.
    staleTime: 60_000,
  });

  return {
    regionFeatures: query.data ?? EMPTY,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
  };
}
